import { useEffect, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import type { GifResult } from '@avslack/shared';
import { searchGifs } from '../lib/api';
import { usePrefersReducedMotion } from '../lib/motion';

/**
 * GIF-kereso.
 *
 * A keresest a SAJAT szerverunk vegzi (lasd modules/gifs): a Giphy-kulcs nem
 * kerul a bongeszobe, es a tartalomszurest sem a kliens kere donti el.
 *
 * A valasztas AZONNAL kuld. Egy GIF onmagaban uzenet - a szerkesztobe szurva
 * csak plusz egy Enterre lenne szukseg, es a kozben beirt szoveg mellett
 * furcsan mutatna.
 */
export function GifPicker({
  onPick,
  focusOnClose,
  children,
}: {
  onPick: (gif: GifResult) => void;
  /** Hova menjen a fokusz bezaraskor - lasd az EmojiPicker azonos mezojet. */
  focusOnClose?: () => void;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [gifs, setGifs] = useState<GifResult[]>([]);
  const [loading, setLoading] = useState(false);
  const reduceMotion = usePrefersReducedMotion();

  /**
   * Kesleltetett kereses.
   *
   * Betunkent kerdezni a Giphy-t pazarlas es lassu is: a 300 ms alatt begepelt
   * betuk egyetlen keresesse allnak ossze.
   */
  useEffect(() => {
    if (!open) return;

    let canceled = false;
    setLoading(true);

    const timer = setTimeout(() => {
      void searchGifs(query)
        .then((result) => {
          if (!canceled) setGifs(result);
        })
        .catch(() => {
          if (!canceled) setGifs([]);
        })
        .finally(() => {
          if (!canceled) setLoading(false);
        });
    }, 300);

    return () => {
      canceled = true;
      clearTimeout(timer);
    };
  }, [open, query]);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="start"
          sideOffset={8}
          onCloseAutoFocus={
            focusOnClose
              ? (event) => {
                  event.preventDefault();
                  focusOnClose();
                }
              : undefined
          }
          className="animate-fade-rise z-50 w-80 rounded-card border border-border-soft bg-surface-raised p-2 shadow-(--shadow-modal)"
        >
          <input
            aria-label="GIF keresése"
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="GIF keresés…"
            className="mb-2 w-full rounded-control border border-border bg-surface px-2.5 py-1.5 text-sm outline-none focus:border-accent"
          />

          <div className="h-64 overflow-y-auto">
            {/* A live regio mindig jelen van, csak a szovege valtozik. */}
            <div role="status">
              {loading && gifs.length === 0 && (
                <p className="py-10 text-center text-xs text-ink-faint">Keresünk…</p>
              )}

              {!loading && gifs.length === 0 && (
                <p className="py-10 text-center text-xs text-ink-faint">
                  {query.trim() ? 'Nincs találat.' : 'A GIF-kereső most nem elérhető.'}
                </p>
              )}
            </div>

            {/* Ket oszlop, valtozo magassaggal - a GIF-ek aranya nagyon szoro. */}
            <div className="columns-2 gap-1.5 [column-fill:_balance]">
              {gifs.map((gif) => (
                <button
                  key={gif.id}
                  type="button"
                  title={gif.title ?? 'GIF'}
                  onClick={() => {
                    onPick(gif);
                    setOpen(false);
                  }}
                  className="mb-1.5 block w-full overflow-hidden rounded-control transition-opacity hover:opacity-80"
                >
                  <img
                    src={(reduceMotion && gif.previewStillUrl) || gif.previewUrl}
                    alt={gif.title ?? 'GIF'}
                    loading="lazy"
                    width={gif.width}
                    height={gif.height}
                    className="w-full"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* A Giphy hasznalati feltetele elvarja a forras feltuntetéset. */}
          <p className="pt-1.5 text-center text-2xs text-ink-faint">GIF-ek: Giphy</p>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
